/**
 * Extraction Results Table Component
 * 
 * Displays extracted BOM data in a table format with
 * inline editing, filtering, and export capabilities.
 */

import React, { useState, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';

// BOM schema fields for table columns
const BOM_COLUMNS = [
  { key: 'part_number', label: 'Part #', width: 100 },
  { key: 'description', label: 'Description', width: 200 },
  { key: 'quantity', label: 'Qty', width: 60, type: 'number' },
  { key: 'unit', label: 'Unit', width: 50 },
  { key: 'material', label: 'Material', width: 120 },
  { key: 'size', label: 'Size', width: 80 },
  { key: 'schedule', label: 'Schedule', width: 70 },
  { key: 'rating', label: 'Rating', width: 70 },
  { key: 'end_connection', label: 'End Conn', width: 80 },
  { key: 'line_number', label: 'Line #', width: 80 },
  { key: 'area', label: 'Area', width: 70 },
  { key: 'remarks', label: 'Remarks', width: 150 }
];

const ExtractionResultsTable = ({ extractions, onRefresh, onExport, compact }) => {
  const { instance, accounts } = useMsal();
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedExtractions, setSelectedExtractions] = useState(new Set());
  const [saving, setSaving] = useState(false);

  // Flatten all extraction items for display
  const allItems = useMemo(() => {
    const items = [];
    extractions.forEach(extraction => {
      const structured = extraction.structured_data;
      if (structured && structured.items) {
        structured.items.forEach((item, index) => {
          items.push({
            ...item,
            _extraction_id: extraction.id,
            _source_file: extraction.source_filename,
            _page: extraction.page_number,
            _item_index: index,
            _status: extraction.status
          });
        });
      }
    });
    return items;
  }, [extractions]);

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (!searchTerm) return allItems;
    
    const term = searchTerm.toLowerCase();
    return allItems.filter(item => {
      return BOM_COLUMNS.some(col => {
        const value = item[col.key];
        return value && String(value).toLowerCase().includes(term);
      }) || 
      item._source_file?.toLowerCase().includes(term);
    });
  }, [allItems, searchTerm]);

  // Group by source file for compact view
  const groupedExtractions = useMemo(() => {
    const groups = {};
    extractions.forEach(ext => {
      const key = ext.source_filename;
      if (!groups[key]) {
        groups[key] = {
          filename: key,
          extractions: [],
          totalItems: 0,
          status: ext.status
        };
      }
      groups[key].extractions.push(ext);
      const itemCount = ext.structured_data?.items?.length || 0;
      groups[key].totalItems += itemCount;
    });
    return Object.values(groups);
  }, [extractions]);

  const toggleExpand = (extractionId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(extractionId)) {
        next.delete(extractionId);
      } else {
        next.add(extractionId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedExtractions.size === extractions.length) {
      setSelectedExtractions(new Set());
    } else {
      setSelectedExtractions(new Set(extractions.map(e => e.id)));
    }
  };

  const toggleSelect = (id) => {
    setSelectedExtractions(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleCellClick = (extractionId, itemIndex, column, value) => {
    setEditingCell({ extractionId, itemIndex, column });
    setEditValue(value || '');
  };

  const handleCellBlur = async () => {
    if (!editingCell) return;
    
    const { extractionId, itemIndex, column } = editingCell;
    const extraction = extractions.find(e => e.id === extractionId);
    
    if (!extraction) {
      setEditingCell(null);
      return;
    }
    
    // Update the structured data
    const updatedStructured = { ...extraction.structured_data };
    if (updatedStructured.items && updatedStructured.items[itemIndex]) {
      const oldValue = updatedStructured.items[itemIndex][column];
      if (oldValue !== editValue) {
        updatedStructured.items[itemIndex][column] = editValue;
        
        setSaving(true);
        try {
          await authenticatedApiCall(
            instance, accounts, 'PUT',
            `/bom-extraction/extractions/${extractionId}`,
            { structuredData: updatedStructured }
          );
          onRefresh();
        } catch (err) {
          console.error('Error saving cell:', err);
        } finally {
          setSaving(false);
        }
      }
    }
    
    setEditingCell(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleCellBlur();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  const handleExportSelected = () => {
    const ids = Array.from(selectedExtractions);
    if (ids.length > 0) {
      onExport(ids);
    }
  };

  const handleDeleteExtraction = async (id) => {
    if (!window.confirm('Delete this extraction?')) return;
    
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/bom-extraction/extractions/${id}`);
      onRefresh();
    } catch (err) {
      console.error('Error deleting extraction:', err);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      completed: { bg: '#d4edda', text: '#155724' },
      failed: { bg: '#f8d7da', text: '#721c24' },
      processing: { bg: '#fff3cd', text: '#856404' },
      pending: { bg: '#e2e3e5', text: '#383d41' },
      reviewed: { bg: '#cce5ff', text: '#004085' }
    };
    const color = colors[status] || colors.pending;
    
    return (
      <span style={{
        ...styles.statusBadge,
        backgroundColor: color.bg,
        color: color.text
      }}>
        {status}
      </span>
    );
  };

  // Compact view for showing on upload step
  if (compact) {
    return (
      <div style={styles.compactContainer}>
        {groupedExtractions.length === 0 ? (
          <p style={styles.emptyMessage}>No previous extractions found.</p>
        ) : (
          <div style={styles.compactList}>
            {groupedExtractions.slice(0, 5).map((group, index) => (
              <div key={index} style={styles.compactItem}>
                <div style={styles.compactInfo}>
                  <span style={styles.compactFilename}>{group.filename}</span>
                  <span style={styles.compactCount}>{group.totalItems} items</span>
                </div>
                {getStatusBadge(group.status)}
              </div>
            ))}
            {groupedExtractions.length > 5 && (
              <p style={styles.moreMessage}>
                and {groupedExtractions.length - 5} more...
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Toolbar */}
      <div style={styles.toolbar}>
        <div style={styles.searchContainer}>
          <input
            type="text"
            style={styles.searchInput}
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              style={styles.clearSearch}
              onClick={() => setSearchTerm('')}
            >
              ✕
            </button>
          )}
        </div>
        
        <div style={styles.toolbarRight}>
          <span style={styles.itemCount}>
            {filteredItems.length} items from {extractions.length} extraction(s)
          </span>
          
          {selectedExtractions.size > 0 && (
            <button
              style={styles.exportButton}
              onClick={handleExportSelected}
            >
              Export Selected ({selectedExtractions.size})
            </button>
          )}
          
          <button
            style={styles.refreshButton}
            onClick={onRefresh}
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Results by extraction */}
      {extractions.length === 0 ? (
        <div style={styles.emptyState}>
          <span style={styles.emptyIcon}>📄</span>
          <h3>No Extractions Yet</h3>
          <p>Upload PDFs and define a region to extract BOM data.</p>
        </div>
      ) : (
        <div style={styles.extractionsList}>
          {extractions.map(extraction => {
            const isExpanded = expandedRows.has(extraction.id);
            const items = extraction.structured_data?.items || [];
            const isSelected = selectedExtractions.has(extraction.id);
            
            return (
              <div key={extraction.id} style={styles.extractionCard}>
                <div style={styles.extractionHeader}>
                  <div style={styles.extractionHeaderLeft}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(extraction.id)}
                      style={styles.checkbox}
                    />
                    <button
                      style={styles.expandButton}
                      onClick={() => toggleExpand(extraction.id)}
                    >
                      {isExpanded ? '▼' : '▶'}
                    </button>
                    <div style={styles.extractionInfo}>
                      <span style={styles.extractionFilename}>
                        {extraction.source_filename}
                      </span>
                      <span style={styles.extractionMeta}>
                        Page {extraction.page_number} • {items.length} items
                      </span>
                    </div>
                  </div>
                  
                  <div style={styles.extractionHeaderRight}>
                    {getStatusBadge(extraction.status)}
                    <button
                      style={styles.deleteButton}
                      onClick={() => handleDeleteExtraction(extraction.id)}
                      title="Delete extraction"
                    >
                      🗑
                    </button>
                  </div>
                </div>
                
                {isExpanded && items.length > 0 && (
                  <div style={styles.tableContainer}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={{ ...styles.th, width: 40 }}>#</th>
                          {BOM_COLUMNS.map(col => (
                            <th key={col.key} style={{ ...styles.th, width: col.width }}>
                              {col.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, idx) => (
                          <tr key={idx} style={styles.tr}>
                            <td style={styles.td}>{idx + 1}</td>
                            {BOM_COLUMNS.map(col => {
                              const isEditing = editingCell?.extractionId === extraction.id && 
                                               editingCell?.itemIndex === idx && 
                                               editingCell?.column === col.key;
                              
                              return (
                                <td 
                                  key={col.key} 
                                  style={{
                                    ...styles.td,
                                    ...(isEditing ? styles.tdEditing : {})
                                  }}
                                  onClick={() => !isEditing && handleCellClick(extraction.id, idx, col.key, item[col.key])}
                                >
                                  {isEditing ? (
                                    <input
                                      type={col.type === 'number' ? 'number' : 'text'}
                                      style={styles.cellInput}
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      onBlur={handleCellBlur}
                                      onKeyDown={handleKeyDown}
                                      autoFocus
                                    />
                                  ) : (
                                    <span style={styles.cellValue}>
                                      {item[col.key] ?? '-'}
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {isExpanded && items.length === 0 && (
                  <div style={styles.noItems}>
                    {extraction.status === 'failed' ? (
                      <>
                        <span style={styles.errorIcon}>⚠️</span>
                        <span>Extraction failed: {extraction.error_message || 'Unknown error'}</span>
                      </>
                    ) : (
                      <span>No items extracted</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {saving && (
        <div style={styles.savingOverlay}>
          <div style={styles.savingSpinner} />
          Saving...
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px'
  },
  searchContainer: {
    position: 'relative',
    flex: '0 1 300px'
  },
  searchInput: {
    width: '100%',
    padding: '8px 32px 8px 12px',
    borderRadius: '6px',
    border: '1px solid #ddd',
    fontSize: '14px',
    boxSizing: 'border-box'
  },
  clearSearch: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#999',
    fontSize: '14px'
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  itemCount: {
    fontSize: '13px',
    color: '#666'
  },
  exportButton: {
    padding: '8px 16px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer'
  },
  refreshButton: {
    padding: '8px 16px',
    backgroundColor: '#f8f9fa',
    color: '#333',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer'
  },
  extractionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  extractionCard: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  extractionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0'
  },
  extractionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  extractionHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer'
  },
  expandButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    padding: '4px'
  },
  extractionInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  extractionFilename: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333'
  },
  extractionMeta: {
    fontSize: '12px',
    color: '#666'
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase'
  },
  deleteButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '4px',
    opacity: 0.6
  },
  tableContainer: {
    overflow: 'auto',
    maxHeight: '400px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px'
  },
  th: {
    padding: '10px 8px',
    textAlign: 'left',
    backgroundColor: '#f0f0f0',
    borderBottom: '2px solid #ddd',
    fontWeight: '600',
    position: 'sticky',
    top: 0,
    whiteSpace: 'nowrap'
  },
  tr: {
    borderBottom: '1px solid #eee'
  },
  td: {
    padding: '8px',
    cursor: 'pointer',
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  tdEditing: {
    padding: '4px',
    backgroundColor: '#fff3cd'
  },
  cellValue: {
    display: 'block'
  },
  cellInput: {
    width: '100%',
    padding: '4px 6px',
    border: '2px solid #007bff',
    borderRadius: '4px',
    fontSize: '13px',
    boxSizing: 'border-box'
  },
  noItems: {
    padding: '20px',
    textAlign: 'center',
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  errorIcon: {
    fontSize: '16px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#666'
  },
  emptyIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px'
  },
  savingOverlay: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: '#333',
    color: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    zIndex: 1000
  },
  savingSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  // Compact view styles
  compactContainer: {
    padding: '0'
  },
  compactList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  compactItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px'
  },
  compactInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  compactFilename: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#333'
  },
  compactCount: {
    fontSize: '11px',
    color: '#666'
  },
  emptyMessage: {
    margin: 0,
    color: '#666',
    fontSize: '13px',
    textAlign: 'center',
    padding: '20px'
  },
  moreMessage: {
    margin: '8px 0 0 0',
    color: '#666',
    fontSize: '12px',
    textAlign: 'center'
  }
};

export default ExtractionResultsTable;

